/**
 * @file Emails hooks — recipient count preview, candidate list, campaign
 *       reads, suppressions, and the send / resend mutations.
 * @module @labcraft/admin/ui/hooks/use-emails
 *
 * The compose surface needs two views of the same filter set: the
 * authoritative recipient *count* (`useRecipientCount`, from the account
 * resolve route) and a page of candidate learners for the multi-select
 * (`useEmailCandidates`, from the learners list route). They're separate
 * queries so the count stays exact even when the candidate list is a
 * single capped page.
 *
 * The send + resend mutations invalidate the campaign-scoped cache on
 * success so the Campaigns list / detail re-fetch with the new campaign.
 *
 * Convention: hooks live in this directory and never call `useQuery` /
 * `useMutation` outside it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Admin } from '../../client/index.ts'
import type { LearnerListFilters } from '../../shared/routes.ts'
import type {
  CampaignDetail,
  EmailRecipientFilters,
  ListCampaignsResponse,
  ListEmailEventsResponse,
  ListSuppressionsResponse,
  ListSendersResponse,
  ResolveRecipientsResponse,
  SendBroadcastRequest,
  SendBroadcastResponse,
  TestSendRequest,
  TestSendResponse,
} from '../../shared/emails_routes.ts'
import {
  qkEmailCampaign,
  qkEmailCampaigns,
  qkEmailCandidates,
  qkEmailHistory,
  qkEmailRecipientCount,
  qkEmailSenders,
  qkEmailSuppressions,
} from './query_keys.ts'
import { useSettingsValues } from './use_settings.ts'

/**
 * The operator-configured default From address for a send in the given lane,
 * for the compose/review preview header, so it shows the address the email is
 * actually sent from. Marketing → `campaign_from` (Resend, `@news.labcraft.dev`);
 * transactional → `smtp_from` (Resend, `@mail.labcraft.dev` — the wire key
 * predates the #1690 SMTP→Resend cutover and is kept for stability).
 * `undefined` while settings load or if the key is unset.
 *
 * @param classification - The send-type lane whose default From to resolve.
 * @returns The From header string, or `undefined`.
 */
export function useEmailFrom(
  classification: 'transactional' | 'marketing',
): string | undefined {
  const values = useSettingsValues()
  const wireKey = classification === 'marketing' ? 'campaign_from' : 'smtp_from'
  const raw = values.data?.values?.email?.[wireKey]
  return typeof raw === 'string' ? raw : undefined
}

/**
 * Subscribe to the authoritative recipient count for a filter set (the
 * compose preview). Exact over the whole table — not a page size.
 *
 * @param filters - The recipient-subset filter set.
 */
export function useRecipientCount(filters: EmailRecipientFilters) {
  const client = useClient<Admin>()
  return useQuery<ResolveRecipientsResponse>({
    queryKey: qkEmailRecipientCount(filters),
    queryFn: () => client.emails.resolveRecipientCount(filters),
  })
}

/**
 * Subscribe to a page of candidate learners for the compose
 * multi-select, matching the given filter set. Reuses the admin
 * learners-list route (extended with the compose filters in Part 7).
 *
 * @param filters - The recipient-subset filter set.
 */
export function useEmailCandidates(filters: LearnerListFilters) {
  const client = useClient<Admin>()
  return useQuery({
    queryKey: qkEmailCandidates(filters),
    queryFn: () => client.learners.list(filters),
  })
}

/** Subscribe to the campaign list (newest first). */
export function useCampaigns() {
  const client = useClient<Admin>()
  return useQuery<ListCampaignsResponse>({
    queryKey: qkEmailCampaigns,
    queryFn: () => client.emails.listCampaigns(),
  })
}

/**
 * Subscribe to the unified email-history read model — every one-off +
 * campaign event, newest send first (#1606 Part 11). The History surface and
 * the Individual-compose per-learner history column both read from it.
 *
 * @returns The react-query result over `{ events }`.
 */
export function useEmailHistory() {
  const client = useClient<Admin>()
  return useQuery<ListEmailEventsResponse>({
    queryKey: qkEmailHistory,
    queryFn: () => client.emails.listEvents(),
  })
}

/**
 * Subscribe to one campaign's per-recipient delivery detail.
 *
 * @param id - Campaign id.
 */
export function useCampaign(id: string) {
  const client = useClient<Admin>()
  return useQuery<CampaignDetail>({
    queryKey: qkEmailCampaign(id),
    queryFn: () => client.emails.getCampaign(id),
    // While the outbox worker is still delivering (#1627), poll so the
    // sent / delivered / failed counts climb live; stop once every recipient
    // has left the pending state.
    refetchInterval: query => {
      const detail = query.state.data
      if (detail === undefined) return false
      const delivering = detail.recipients.some(r => r.sendStatus === 'pending')
      return delivering ? 2_000 : false
    },
  })
}

/**
 * Subscribe to the selectable sender identities + the default Reply-To (#1616)
 * — the source for the compose From picker (filtered by send-type) and the
 * Reply-To default.
 *
 * @returns The react-query result over `{ senders, defaultReplyTo }`.
 */
export function useEmailSenders() {
  const client = useClient<Admin>()
  return useQuery<ListSendersResponse>({
    queryKey: qkEmailSenders,
    queryFn: () => client.emails.listSenders(),
  })
}

/** Subscribe to the suppression (opt-out) list. */
export function useSuppressions() {
  const client = useClient<Admin>()
  return useQuery<ListSuppressionsResponse>({
    queryKey: qkEmailSuppressions,
    queryFn: () => client.emails.listSuppressions(),
  })
}

/**
 * Compose + send a broadcast. On success, invalidates the campaign list
 * so the new campaign appears without a manual refresh.
 */
export function useSendBroadcastMutation() {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  return useMutation<SendBroadcastResponse, Error, SendBroadcastRequest>({
    mutationFn: args => client.emails.send(args),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qkEmailCampaigns })
    },
  })
}

/**
 * Send a preview copy of the composed message to one address (#1606 Part 13).
 * A standalone mutation — no cache invalidation, since a test-send writes no
 * campaign / tracking state.
 *
 * @returns The mutation over `{ to, subject, body, format?, kind? }`.
 */
export function useTestSendMutation() {
  const client = useClient<Admin>()
  return useMutation<TestSendResponse, Error, TestSendRequest>({
    mutationFn: args => client.emails.testSend(args),
  })
}

/**
 * Re-send a campaign to its bounced / failed recipients. On success,
 * invalidates the campaign list + the source campaign's detail so both
 * re-fetch with the fresh state.
 */
export function useResendCampaignMutation() {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  return useMutation<SendBroadcastResponse, Error, string>({
    mutationFn: id => client.emails.resendCampaign(id),
    onSuccess: (_res, id) => {
      void queryClient.invalidateQueries({ queryKey: qkEmailCampaigns })
      void queryClient.invalidateQueries({ queryKey: qkEmailCampaign(id) })
    },
  })
}
